"use client";

import { FormSubmit } from "@/components/bulkimp-form/form-submit";
import { FormTextarea } from "@/components/bulkimp-form/form-textarea";
import { Button } from "@heroui/react";
import { Skeleton } from "@/components/ui/skeleton";
import { AlignLeft } from "lucide-react";
import { useState, useRef, ElementRef, useEffect } from "react";
import { useEventListener, useOnClickOutside } from "usehooks-ts";
import { toast } from "sonner";
import { Doc } from "@/convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { DescriptionWithLinks } from "@/components/shared/description-with-links";
interface DescriptionProps {
  data: Doc<"todos">;
}
export const Description = ({
  data
}: DescriptionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(data.description);
  const textareaRef = useRef<ElementRef<"textarea">>(null);
  const formRef = useRef<ElementRef<"form">>(null);
  const [isLoading, setIsLoading] = useState(false);
  const updateCardAllInfoMutation = useMutation(api.pmAi.todos.updateCardAllInfo);

  // Subscribe to real-time todo updates
  const todoResponse = useQuery(api.pmAi.todos.getTodoById, {
    _id: data._id
  });

  // Update local state when todo data changes
  useEffect(() => {
    if (todoResponse?.data?.description !== description) {
      setDescription(todoResponse?.data?.description || "");
    }
  }, [todoResponse?.data?.description]);
  const enableEditing = () => {
    setIsEditing(true);
    setTimeout(() => {
      textareaRef.current?.focus();
    });
  };
  const disableEditing = () => {
    setIsEditing(false);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      disableEditing();
    }
  };
  useEventListener("keydown", onKeyDown);
  useOnClickOutside(formRef, disableEditing);
  const onSubmit = async (formData: FormData) => {
    try {
      setIsLoading(true);
      const newDescription = formData.get("description") as string;
      const result = await updateCardAllInfoMutation({
        _id: data._id,
        title: data.title,
        description: newDescription,
        priority: data.priority as "Low" | "Normal" | "High" | "Urgent",
        dueDate: data.dueDate,
        labelIds: data.labelIds,
        assignedUserId: data.assignedUserId
      });
      if (!result?.data) {
        toast.error(result?.error?.message || "Failed to update description");
        return;
      }
      setDescription(newDescription);
      toast.success("Description updated");
      disableEditing();
    } catch (error) {
      console.error("[Description.onSubmit] - error:", error);
      toast.error("An error occurred while updating the description");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state if we're waiting for initial todo data
  if (!todoResponse?.data) {
    return <Description.Skeleton />;
  }

  // Show error state if there's an error
  if (todoResponse?.error) {
    return <div className="p-4 text-sm text-red-500">
        Error loading description: {todoResponse.error.message}
      </div>;
  }
  return <div className="flex w-full items-start gap-x-3" data-sentry-component="Description" data-sentry-source-file="description.tsx">
      <AlignLeft className="mt-0.5 h-5 w-5 text-neutral-700 dark:text-neutral-300" data-sentry-element="AlignLeft" data-sentry-source-file="description.tsx" />
      <div className="w-full">
        <p className="mb-2 font-semibold text-neutral-700 dark:text-neutral-300">
          Description
        </p>
        {isEditing ? <form ref={formRef} className="space-y-2" action={onSubmit}>
            <FormTextarea id="description" ref={textareaRef} className="mt-2 w-full" placeholder="Add a more detailed description..." defaultValue={description || undefined} />
            <div className="flex items-center gap-x-2">
              <Button type="submit" variant="solid" color="primary" size="sm" isDisabled={isLoading} isLoading={isLoading}>
                Save
              </Button>
              <Button type="button" onClick={disableEditing} isDisabled={isLoading} variant={"ghost"} size="sm">
                Cancel
              </Button>
            </div>
          </form> : <div onClick={enableEditing} role="button" className="min-h-[78px] whitespace-pre-line rounded-md bg-neutral-100 px-3.5 py-3 text-sm font-medium dark:bg-neutral-700">
            {description ? <DescriptionWithLinks text={description} /> : "Add a more detailed description..."}
          </div>}
      </div>
    </div>;
};
Description.Skeleton = function DescriptionSkeleton() {
  return <div className="flex w-full items-start gap-x-3">
      <Skeleton className="h-6 w-6 bg-neutral-200 dark:bg-neutral-700" />
      <div className="w-full">
        <Skeleton className="mb-2 h-6 w-24 bg-neutral-200 dark:bg-neutral-700" />
        <Skeleton className="h-[78px] w-full bg-neutral-200 dark:bg-neutral-700" />
      </div>
    </div>;
};